import cn from "classnames"
import Cookies from "js-cookie"
import { throttle } from "lodash-es"
import { memoize } from "proxy-memoize"
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useSelector, connect, useDispatch } from "react-redux"
import Canvas from "./canvas"
import ResetButton from "./reset_button"
import ResetTooltip from "./reset_tooltip"
import RestoreBanner from "./restore_banner"
import SignPreview, { useSignPreview } from "@/common/components/sign_preview"
import useMedia from "@/common/hooks/use_media"
import { sizeUnit } from "@/common/locales"
import storageLocal from "@/common/storage"
import tailwindTheme from "@/common/styles/theme.module.css"
import { isTablet } from "@/common/util"
import type Sign from "@/signomatic/app/sign"
import { type ActiveObjectInfo, type SignViewportState } from "@/signomatic/app/sign"
import { type SignHistoryState } from "@/signomatic/app/sign_history"
import ControlBar from "@/signomatic/components/control_bar"
import DoubleSidedControls from "@/signomatic/components/double_sided_controls"
import ObjectInfo from "@/signomatic/components/object_info"
import Price from "@/signomatic/components/price"
import RowButton from "@/signomatic/components/row_button"
import Sharing from "@/signomatic/components/sharing"
import SubmitButton from "@/signomatic/components/submit_button"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import { macOs, ios } from "@/signomatic/util"
import { Redo, Undo, More, EyeOpenOutline, EyeClosedOutline, ExcelImportOutline, ZoomOut } from "@/skylticons"

interface PreviewLinksProps {
  sign: Sign
  signType: string
  signState: SignState | null
}

const PreviewLinks: React.FC<PreviewLinksProps> = ({ sign, signType, signState }) => {
  const { t } = useTranslation()
  const [openPreview, previewProps] = useSignPreview()
  const preview = <SignPreview {...previewProps} />
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensLg})`)

  if (previewProps.open) {
    return (
      <RowButton className="hide_background_preview">
        {preview}
        <EyeClosedOutline />
        {desktop && <span className="paragraph-xs mb-0">{t(`misc.close_preview`)}</span>}
      </RowButton>
    )
  }

  return (
    <RowButton
      className={cn("show_background_preview")}
      onClick={async () => {
        openPreview({
          svg: (await sign.toSVG())[sign.state.side === 0 ? 0 : 1],
          transparent: true,
          withBackground: true,
          signWidth: sizeUnit === "in" ? sign.state.widthIn : sign.state.width,
          signHeight: sizeUnit === "in" ? sign.state.heightIn : sign.state.height,
          signType,
          metaProduct: signState?.metaProduct,
          fixMethod: signState?.fixmethod,
        })
      }}
      data-gtm="preview"
    >
      {preview}
      <EyeOpenOutline />
      {desktop && <span className="paragraph-xs mb-0">{t(`misc.open_preview`)}</span>}
    </RowButton>
  )
}

interface BackgroundProps {
  sign: Sign
}

interface BackgroundState {
  signType: string
  signState: SignState | null
  history: SignHistoryState | null
  metaProduct: string
  activeObject: SignObjectState | null
  toolboxHeight: number | null
  toolbox: string | null
}

const backgroundSelector = memoize(
  ({ signType, signState, history, metaProduct, activeObject, toolboxHeight, toolbox }: State) => ({
    signType,
    signState,
    history,
    metaProduct,
    activeObject,
    toolboxHeight,
    toolbox,
  })
)

const Background: React.FC<BackgroundProps> = ({ sign }) => {
  const { signType, signState, history, metaProduct, activeObject, toolboxHeight, toolbox } = useSelector<
    State,
    BackgroundState
  >(backgroundSelector)
  const { t } = useTranslation()
  const [objectInfo, setObjectInfo] = useState<ActiveObjectInfo | null>(null)
  const [screenInnerHeight, setScreenInnerHeight] = useState(0)
  const [objectState, setObjectState] = useState<SignObjectState | null>(null)
  const dispatch = useDispatch()
  const [canvasHeight, setCanvasHeight] = useState<number | undefined>()
  const [viewportState, setViewportState] = useState<SignViewportState | null>(null)
  const [showResetTooltip, setShowResetTooltip] = useState(false)
  const [showMoreButtons, setShowMoreButtons] = useState(false)
  const cookieConsent = Cookies.get("cookies_consented")

  const updateInfo = useMemo(
    () =>
      throttle((info: ActiveObjectInfo) => {
        setObjectInfo(info)
      }, 200),
    [setObjectInfo]
  )

  const handleKeys = useCallback(
    (e: KeyboardEvent) => {
      const meta = macOs() || ios() ? e.metaKey : e.ctrlKey

      if (meta && (e.key === "y" || (e.shiftKey && e.key === "z")) && history?.hasRedo) {
        e.preventDefault()
        sign.redo()
        return
      }

      if (meta && e.key === "z" && history?.hasUndo) {
        e.preventDefault()
        sign.undo()
        return
      }

      const target = e.target as HTMLElement | undefined

      if (signType !== "contour_cut" && target?.nodeName !== "TEXTAREA" && target?.nodeName != "INPUT") {
        if (meta && e.key === "c" && activeObject) {
          e.preventDefault()
          e.stopPropagation()
          setObjectState(activeObject)
          return
        }

        if (meta && e.key === "v" && objectState) {
          e.preventDefault()
          e.stopPropagation()
          sign.objectPaste(objectState)
          return
        }

        if (meta && e.key === "d" && activeObject) {
          e.preventDefault()
          e.stopPropagation()
          sign.duplicateObject(activeObject, sign.centerX, sign.centerY)
          return
        }
      }
    },
    [sign, history?.hasRedo, history?.hasUndo, activeObject, objectState, signType]
  )

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (e.target instanceof Element && !e.target.closest(".excel-import")) {
      setShowMoreButtons(false)
    }
  }, [])

  useEffect(() => {
    const closeMoreBtnsMenu = (state: SignObjectState | null) => {
      if (!state) {
        setShowMoreButtons(false)
      }
    }

    sign.onActiveInfoChanged.subscribe(updateInfo)
    document.addEventListener("keydown", handleKeys, { capture: true })
    document.addEventListener("mouseup", handleMouseUp)
    setScreenInnerHeight(window.innerHeight)
    sign.onViewportStateChanged.subscribe(setViewportState)
    sign.onActiveChanged.subscribe(closeMoreBtnsMenu)

    return () => {
      sign.onActiveInfoChanged.unsubscribe(updateInfo)
      sign.onViewportStateChanged.unsubscribe(setViewportState)
      document.removeEventListener("keydown", handleKeys, { capture: true })
      document.removeEventListener("mouseup", handleMouseUp)
      sign.onActiveChanged.unsubscribe(closeMoreBtnsMenu)
    }
  }, [
    updateInfo,
    handleKeys,
    handleMouseUp,
    sign.onActiveInfoChanged,
    sign.onViewportStateChanged,
    sign.onActiveChanged,
  ])

  useEffect(() => {
    if (isTablet()) {
      if (toolboxHeight) {
        setCanvasHeight(screenInnerHeight - toolboxHeight)
      } else {
        setCanvasHeight(undefined)
      }
    }
  }, [toolboxHeight, screenInnerHeight])

  useEffect(() => {
    setShowResetTooltip(sign.canShowResetTooltip)
  }, [sign.canShowResetTooltip])

  useEffect(() => {
    const handler = () => {
      if (cookieConsent) {
        storageLocal.setItem("signomatic_visited", "true")
      } else {
        storageLocal.removeItem("signomatic_visited")
      }
    }

    window.addEventListener("beforeunload", handler)

    return () => window.removeEventListener("beforeunload", handler)
  }, [cookieConsent])

  const desktop = useMedia(`(min-width: ${tailwindTheme.screensLg})`)
  const mobileXs = useMedia(`(max-width: ${tailwindTheme.screensXs})`)
  const noExcelImport = signType == "sticker" && signState?.shape == "contour" && signState?.objects.length > 1

  const zoom = viewportState?.zoom || 1

  const openResetTooltip = () => {
    if (Cookies.get("reset_tooltip_closed") === "true") return

    setShowResetTooltip(true)
  }

  const closeResetTooltip = () => {
    Cookies.set("reset_tooltip_closed", "true")
    setShowResetTooltip(false)
  }

  const [excelIntersecting, setExcelIntersecting] = useState(false)

  const buttonRow = useRef<HTMLDivElement>(null)
  const excelButton = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (buttonRow.current) {
      const handleIntersection: IntersectionObserverCallback = entries => {
        entries.forEach(entry => {
          setExcelIntersecting(entry.isIntersecting)
        })
      }

      const observer = new IntersectionObserver(handleIntersection, {
        root: buttonRow.current,
        threshold: 1,
      })

      if (excelButton.current) {
        observer.observe(excelButton.current)
      }

      return () => observer.disconnect()
    }

    return
  }, [noExcelImport, desktop])

  return (
    <div className="background min-w-0">
      {desktop && (
        <div
          className={cn("top-row first-row min-w-0", {
            hide: toolboxHeight && toolboxHeight > 0,
            "pointer-events-none cursor-default opacity-50": signType === "",
          })}
        >
          <div className="left-col min-w-0 p-1 lg:p-0" ref={buttonRow}>
            <PreviewLinks sign={sign} signType={signType} signState={signState} />
            <Sharing sign={sign} />
            <span className="divider" />
            <div className="h-full">
              <ResetButton sign={sign} />
              {showResetTooltip && <ResetTooltip closeTooltip={closeResetTooltip} />}
            </div>
            <span className="divider" />
            <RowButton
              iconButton
              className="undo"
              disabled={!history?.hasUndo}
              onClick={() => sign.undo()}
              data-gtm="undo"
            >
              <Undo />
              <span className="paragraph-xs mb-0">{t("toolbox.undo")}</span>
            </RowButton>
            <RowButton
              iconButton
              className="redo"
              disabled={!history?.hasRedo}
              onClick={() => sign.redo()}
              data-gtm="redo"
            >
              <Redo />
              <span className="paragraph-xs mb-0">{t("toolbox.redo")}</span>
            </RowButton>
            {!noExcelImport && (
              <>
                <span className="divider" />
                {!excelIntersecting ? (
                  <div className="flex h-full w-[42px] items-center">
                    <RowButton
                      iconButton
                      className={cn("more icon-only rounded", { "!bg-white-10": showMoreButtons })}
                      onClick={() => setShowMoreButtons(!showMoreButtons)}
                    >
                      <More />
                    </RowButton>
                    {showMoreButtons && (
                      <div className="relative top-[48px] z-50 -translate-x-full">
                        <div className="mx-0 flex h-12 rounded border bg-white p-1">
                          <RowButton
                            iconButton
                            className="excel-import text-nowrap"
                            onClick={() => {
                              dispatch(actions.openFileImport())
                              setShowMoreButtons(false)
                            }}
                            data-gtm="open-excel-import"
                          >
                            <ExcelImportOutline />
                            <span className="paragraph-xs mb-0">{t("excel_import.import_via_excel")}</span>
                          </RowButton>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
                <RowButton
                  iconButton
                  className={cn("excel-import text-nowrap", { invisible: !excelIntersecting })}
                  onClick={() => dispatch(actions.openFileImport())}
                  data-gtm="open-excel-import"
                  ref={excelButton}
                >
                  <ExcelImportOutline />
                  <span className="paragraph-xs mb-0">{t("excel_import.import_via_excel")}</span>
                </RowButton>
              </>
            )}
            <RestoreBanner sign={sign} />
          </div>

          <div className="right-col">
            {signType !== "" && desktop && objectInfo ? <ObjectInfo sign={sign} objectInfo={objectInfo} /> : null}
          </div>
        </div>
      )}

      {(signType === "double_sided_sign" || !desktop) && (
        <div className={cn("top-row second-row", { hide: toolboxHeight && toolboxHeight > 0 })}>
          <div className="left-col p-1 lg:p-0">
            {mobileXs && signType != "double_sided_sign" && (
              <RowButton
                iconButton
                className="undo button button-large button-subtle icon-only m-0"
                tooltip="toolbox.undo"
                disabled={!history?.hasUndo}
                onClick={() => sign.undo()}
                data-gtm="undo"
              >
                <Undo />
              </RowButton>
            )}
            {!mobileXs && signType != "double_sided_sign" && (
              <div className="h-full">
                <ResetButton sign={sign} buttonClasses="button button-large button-subtle button-error icon-only m-0" />
                {showResetTooltip && <ResetTooltip closeTooltip={closeResetTooltip} />}
              </div>
            )}
            {!mobileXs && !desktop && signType != "double_sided_sign" && (
              <RowButton
                iconButton
                className="undo button button-large button-subtle icon-only m-0 ml-0 sm:!ml-2"
                tooltip={t("toolbox.undo")}
                disabled={!history?.hasUndo}
                onClick={() => sign.undo()}
                data-gtm="undo"
              >
                <Undo />
              </RowButton>
            )}
            {!mobileXs && !desktop && signType != "double_sided_sign" && (
              <RowButton
                iconButton
                className="redo button button-large button-subtle icon-only m-0 ml-0 sm:!ml-2"
                tooltip={t("toolbox.redo")}
                disabled={!history?.hasRedo}
                onClick={() => sign.redo()}
                data-gtm="redo"
              >
                <Redo />
              </RowButton>
            )}
            {!desktop && (
              <RowButton
                iconButton
                className="button button-large button-subtle icon-only m-0 ml-0 sm:!ml-2"
                onClick={() => dispatch(actions.setToolbox("submenu"))}
              >
                <More />
              </RowButton>
            )}
            {!desktop && zoom > 1 && (
              <RowButton
                className={cn("zoom-out")}
                tooltip={t("toolbox.zoom_out")}
                onClick={() => sign.updateScale()}
                data-gtm="zoom-out"
              >
                <ZoomOut />
              </RowButton>
            )}
            {signType === "double_sided_sign" && (
              <DoubleSidedControls
                sign={sign}
                signState={signState}
                buttonClasses="!w-auto button button-large button-subtle ml-0 sm:!ml-2"
              />
            )}
          </div>
          <div className="right-col w-full">{!desktop && <Price sign={sign} />}</div>
          <RestoreBanner sign={sign} />
        </div>
      )}
      {signType !== "" && (
        <>
          <div className="canvas">
            <ControlBar sign={sign} />
            <Canvas sign={sign} height={canvasHeight} />
          </div>
          <div className={cn("bottom h-16 lg:-ml-[104px] xl:-ml-36", { "lg:!-ml-[425px] xl:!-ml-[529px]": !!toolbox })}>
            {desktop && (
              <div id="sign-type">
                <h1 className="heading-five mb-0 break-words pr-2 lg:max-w-[144px] xl:max-w-[256px] xl:pr-6 xl:text-xl">
                  {t(`sign_type.${metaProduct ? metaProduct : signType}`)}
                </h1>
              </div>
            )}
            <SubmitButton sign={sign} showResetTooltip={openResetTooltip} />
          </div>
        </>
      )}
    </div>
  )
}

export default connect(({ activeObject, toolboxHeight }: State) => ({ activeObject, toolboxHeight }))(Background)
